import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../api";
import { AddForm, ConfirmDialog, Find, IconButton, matches, SectionHead, ShowMore, useShowMore } from "./Section";
import { ErrorState, PageSkeleton } from "../shell/states";
import { useQueryParam } from "../urlState";

// Administration → Schools, Content providers, Platform (the Access Map plan, change 4a), and the
// claimed-domains panel the Districts screen shares.
//
// Every configurator sees every organization: configurators find things by seeing them. What a
// person may CHANGE comes with each organization (`can`), and an action they may not take is shown
// disabled with the reason, never hidden. The database decides regardless (migration 0077).
//
// Tenants are information here. A tenant locks data rows; an organization is who people belong
// to. Each organization says which tenant its data lives in, and nothing on these screens edits a
// tenant: a school joins a district on the Districts screen, which writes both. `public` is a
// tenant every signed-in person reads, and nobody's home.

export interface Domain {
  domain: string;
  allow_access: boolean;
  sign_in_provider: string;
  claimed_at: string | null;
  claimed_by: string | null;
}

export interface Org {
  organization_id: string;
  kind: "district" | "school" | "content_provider" | "team" | "platform";
  name: string;
  parent_organization_id: string | null;
  home_tenant_id: string | null;
  tenant_name: string | null;
  tenant_type: string | null;
  tenant_schools: number;
  federal_id: string | null;
  school_id: string | null;
  domains: Domain[];
  children: string[];
  // The districts this organization is a partner of, and (a district's) its partners.
  holds: Tenancy[];
  shared_with: Tenancy[];
  can: { claim_domain: boolean; domain_settings: boolean; add_team: boolean; share: boolean;
         why_not: string | null };
}

/** One organization holding one tenant (0081). */
export interface Tenancy {
  grant_id: string;
  organization_id: string;
  organization_name: string;
  tenant_id: string;
  tenant_name: string;
  active_from: string | null;
  home: boolean;
}

export interface Tenant {
  tenant_id: string;
  name: string;
  tenant_type: string;
  schools: number;
  home_of: string | null;
}

interface Choice { id: string; label: string }

export interface OrgData {
  organizations: Org[];
  tenants: Tenant[];
  // What the caller configures: whether they operate, and districts and providers by id.
  powers: { platform: boolean; districts: string[]; providers: string[] };
  sign_in_choices: Choice[];
}

export const said = (e: unknown) => e instanceof ApiError ? (e.detail ?? e.message) : "That didn't work.";

export function useOrganizations() {
  const [data, setData] = useState<OrgData | null>(null);
  const [error, setError] = useState<{ status: number; message: string } | null>(null);
  const reload = useCallback(() => {
    api.get<OrgData>("/organizations")
      .then((d) => { setData(d); setError(null); })
      .catch((e: unknown) => setError(e instanceof ApiError
        ? { status: e.status, message: e.detail ?? e.message }
        : { status: 0, message: "could not be read" }));
  }, []);
  useEffect(reload, [reload]);
  return { data, error, reload };
}

/** A sign-in method's name. Pure, so it is tested. */
export function providerLabel(id: string, choices: Choice[]): string {
  const known = choices.find((c) => c.id === id);
  if (known) return known.label;
  if (id.startsWith("oidc.") || id.startsWith("saml.")) return `Their own sign-in (${id})`;
  return id;
}

/** Where an organization's data lives, in one sentence. Pure, so it is tested. */
export function tenantLine(o: Pick<Org, "kind" | "home_tenant_id" | "tenant_name"
                                     | "tenant_schools">): string {
  const tenant = o.tenant_name ? `${o.tenant_name} (${o.home_tenant_id})` : o.home_tenant_id;
  const schools = o.tenant_schools === 1 ? "1 school" : `${o.tenant_schools} schools`;
  switch (o.kind) {
    case "district":
      return `Data lives in the ${tenant} tenant, which covers ${schools}. `
        + "Its people also read Public, as everyone does.";
    case "school":
      return `Data lives in its district's tenant, ${tenant}.`;
    case "content_provider":
      return o.home_tenant_id
        ? `Its curricula, rubrics and tasks live in the ${tenant} tenant. Districts reach them by `
          + "license, or where they are free."
        : "No tenant yet: it gets one when it starts its first curriculum.";
    case "team":
      return "No tenant: a content team groups a content provider's people.";
    default:
      return "No tenant: the platform owns no data.";
  }
}

export function Refused({ title, error }: { title: string;
                                             error: { status: number; message: string } }) {
  if (error.status !== 403) return <ErrorState />;
  return (
    <div className="gl">
      <h2>{title}</h2>
      <p className="gl-lede">This screen is for configurators and operators.</p>
    </div>
  );
}

// --------------------------------------------------------------------------- claimed domains

/** What releasing a domain does, for the confirmation. Pure, so it is tested. */
export function releaseConsequence(domain: string, org: string, allowAccess: boolean): string {
  return allowAccess
    ? `People at ${domain} will no longer be let in because of their address. Anyone who got in `
      + `that way needs an invitation to ${org}.`
    : `${domain} will no longer belong to ${org}, and any organization can then claim it.`;
}

export function DomainsPanel({ org, choices, onChanged }: {
  org: Org; choices: Choice[]; onChanged: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [domain, setDomain] = useState("");
  const [provider, setProvider] = useState(choices[0]?.id ?? "google.com");
  const [allow, setAllow] = useState(false);
  const [releasing, setReleasing] = useState<Domain | null>(null);

  async function act(run: () => Promise<unknown>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      await run();
      onChanged();
      return true;
    } catch (e) {
      setError(said(e));
      return false;
    } finally {
      setBusy(false);
    }
  }

  function close() {
    setAdding(false);
    setDomain("");
    setAllow(false);
    setError(null);
  }

  const settings = org.can.domain_settings;
  const claimWhy = org.can.claim_domain ? null
    : "Claiming a domain is for an operator, after confirming the organization owns it.";
  return (
    <div className="pp-panel">
      <SectionHead title="Claimed domains" addLabel={`Claim a domain for ${org.name}`}
                   adding={adding} onAdd={() => (adding ? close() : setAdding(true))}
                   disabledWhy={claimWhy} />
      {adding && (
        <AddForm saveLabel="Claim" missing={domain.trim() ? null : "Enter the domain."} busy={busy} error={error}
                 onCancel={close}
                 onSave={() => act(() => api.post(
                   `/organizations/${encodeURIComponent(org.organization_id)}/domains`,
                   { domain: domain.trim(), sign_in_provider: provider, allow_access: allow }))
                   .then((ok) => { if (ok) close(); })}
                 note={<>Allow access lets anyone at the domain sign in with baseline access, never
                   an administrative role. The sign-in method is how they must prove who they
                   are, so a work address used through a personal account is refused.</>}>
          <label>
            Domain
            <input value={domain} placeholder="e.g. lbschools.net" autoFocus
                   onChange={(e) => setDomain(e.target.value)} />
          </label>
          <label>
            Sign-in method
            <select value={provider} onChange={(e) => setProvider(e.target.value)}>
              {choices.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </label>
          <label className="pp-check">
            <span><input type="checkbox" checked={allow}
                         onChange={(e) => setAllow(e.target.checked)} /> Allow access</span>
          </label>
        </AddForm>
      )}
      {org.domains.length === 0 ? (
        <p className="rv-mut">None yet. People reach {org.name} only by invitation.</p>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th>Domain</th>
              <th title="Anyone at the domain may sign in, with baseline access, never an administrative role">
                Allow access</th>
              <th title="How people at the domain must prove who they are">Sign-in method</th>
              <th>Claimed by</th><th />
            </tr>
          </thead>
          <tbody>
            {org.domains.map((d) => (
              <tr key={d.domain}>
                <td><code>{d.domain}</code></td>
                <td>
                  <input type="checkbox" checked={d.allow_access}
                         disabled={busy || !settings} title={org.can.why_not ?? undefined}
                         aria-label={`Allow anyone at ${d.domain} to sign in`}
                         onChange={(e) => act(() => api.patch(
                           `/domains/${encodeURIComponent(d.domain)}`,
                           { allow_access: e.target.checked }))} />
                </td>
                <td>
                  {settings ? (
                    <select value={d.sign_in_provider} disabled={busy}
                            aria-label={`Sign-in method for ${d.domain}`}
                            onChange={(e) => act(() => api.patch(
                              `/domains/${encodeURIComponent(d.domain)}`,
                              { sign_in_provider: e.target.value }))}>
                      {!choices.some((c) => c.id === d.sign_in_provider) && (
                        <option value={d.sign_in_provider}>
                          {providerLabel(d.sign_in_provider, choices)}
                        </option>
                      )}
                      {choices.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                    </select>
                  ) : providerLabel(d.sign_in_provider, choices)}
                </td>
                <td className="d">{d.claimed_by ?? ""}</td>
                <td className="r">
                  <IconButton icon="trash" danger size="row" label={`Release ${d.domain}`}
                              disabled={busy || !org.can.claim_domain}
                              why="Releasing a domain is for an operator."
                              onClick={() => setReleasing(d)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!settings && org.domains.length > 0 && org.can.why_not && (
        <p className="rv-mut">{org.can.why_not}</p>
      )}
      {!adding && error && <p className="inc-error">{error}</p>}
      <ConfirmDialog open={!!releasing} busy={busy} confirmLabel="Release"
                     title={`Release ${releasing?.domain ?? ""}?`}
                     onCancel={() => setReleasing(null)}
                     onConfirm={() => {
                       if (!releasing) return;
                       act(() => api.del(`/domains/${encodeURIComponent(releasing.domain)}`))
                         .then(() => setReleasing(null));
                     }}>
        <p>{releasing && releaseConsequence(releasing.domain, org.name, releasing.allow_access)}</p>
      </ConfirmDialog>
    </div>
  );
}

// --------------------------------------------------------------------------- partners

/** The districts an organization is a partner of, in a sentence. Pure, so it is tested. */
export function reachesLine(holds: Tenancy[]): string | null {
  if (!holds.length) return null;
  const names = holds.map((h) => h.tenant_name);
  return `Partner of ${names.join(", ")}: its configurators may give their own people access `
    + (names.length === 1 ? "there." : "in each.");
}

/** A district's partners: other organizations whose configurators may give their own people
 *  access in the district. The district's own configurators' consent. */
export function SharedWith({ org, orgs, onChanged }: {
  org: Org; orgs: Org[]; onChanged: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [choice, setChoice] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reaches = reachesLine(org.holds ?? []);
  if (org.kind !== "district") return reaches ? <p className="rv-mut">{reaches}</p> : null;

  const already = new Set((org.shared_with ?? []).map((s) => s.organization_id));
  const candidates = orgs.filter((o) =>
    (o.kind === "district" || o.kind === "content_provider")
    && o.organization_id !== org.organization_id && !already.has(o.organization_id));
  const why = "Only this district's own configurators, or an operator, name its partners.";

  async function act(run: () => Promise<unknown>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      await run();
      onChanged();
      return true;
    } catch (e) {
      setError(said(e));
      return false;
    } finally {
      setBusy(false);
    }
  }

  function close() {
    setAdding(false);
    setChoice("");
    setError(null);
  }

  return (
    <div className="pp-panel">
      <SectionHead title="Partners" addLabel={`Name a partner of ${org.name}`}
                   adding={adding} onAdd={() => (adding ? close() : setAdding(true))}
                   disabledWhy={org.can.share ? null : why} />
      {adding && (
        <AddForm saveLabel="Add partner" missing={choice ? null : "Choose an organization."} busy={busy} error={error}
                 onCancel={close}
                 onSave={() => act(() => api.post(
                   `/organizations/${encodeURIComponent(org.organization_id)}/partners`,
                   { organization_id: choice })).then((ok) => { if (ok) close(); })}
                 note={`Their configurators can then give their own people access in `
                   + `${org.name}. End it at any time.`}>
          <label>
            Organization
            <select value={choice} autoFocus onChange={(e) => setChoice(e.target.value)}>
              <option value="">Choose…</option>
              {candidates.map((o) => <option key={o.organization_id} value={o.organization_id}>
                {o.name}</option>)}
            </select>
          </label>
        </AddForm>
      )}
      {(org.shared_with ?? []).length === 0 ? (
        <p className="rv-mut">None yet. Only {org.name}'s own configurators give access in it.</p>
      ) : (
        <ul className="pp-list">
          {org.shared_with.map((s) => (
            <li key={s.grant_id} className="pp-role">
              <span>{s.organization_name}
                {s.active_from && <span className="d"> · since {s.active_from.slice(0, 10)}</span>}</span>
              <IconButton icon="x" size="item" label={`End ${s.organization_name}'s partnership`}
                          disabled={busy || !org.can.share} why={why}
                          onClick={() => act(() => api.del(
                            `/partners/${encodeURIComponent(s.grant_id)}`))} />
            </li>
          ))}
        </ul>
      )}
      {reaches && <p className="rv-mut">{reaches}</p>}
      {!adding && error && <p className="inc-error">{error}</p>}
    </div>
  );
}

// --------------------------------------------------------------------------- Schools

export function SchoolsScreen() {
  const { data, error } = useOrganizations();
  const [typed, setTyped] = useState("");
  const rows = useShowMore();
  if (error) return <Refused title="Schools" error={error} />;
  if (!data) return <PageSkeleton />;
  const byId = new Map(data.organizations.map((o) => [o.organization_id, o]));
  const schools = data.organizations.filter((o) => o.kind === "school");
  const districtOf = (s: Org) => byId.get(s.parent_organization_id ?? "")?.name;
  const shown = schools.filter((s) => matches(typed, s.name, districtOf(s), s.school_id));
  return (
    <div className="gl pp">
      <h2>Schools</h2>
      <p className="gl-lede">Add a school on Districts.</p>
      {schools.length > 0 && (
        <Find label="Find by school, district or NCES id" value={typed}
              onChange={(v) => { setTyped(v); rows.reset(); }}
              shown={shown.length} total={schools.length} />)}
      {schools.length === 0 ? <p className="rv-mut">No schools yet.</p> : (
        <table className="tbl">
          <thead><tr><th>School</th><th>District</th><th>NCES id</th><th>Tenant</th></tr></thead>
          <tbody>
            {shown.slice(0, rows.limit).map((s) => (
              <tr key={s.organization_id}>
                <td>{s.name}</td>
                <td>{districtOf(s) ?? "–"}</td>
                <td><code>{s.school_id}</code></td>
                <td><code>{s.home_tenant_id}</code></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <ShowMore shown={Math.min(rows.limit, shown.length)} total={shown.length} onMore={rows.more} />
    </div>
  );
}

// --------------------------------------------------------------------------- Content providers

export function ContentProviders() {
  const { data, error, reload } = useOrganizations();
  const [open, setOpen] = useQueryParam("provider");
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  if (error) return <Refused title="Content providers" error={error} />;
  if (!data) return <PageSkeleton />;
  const providers = data.organizations.filter((o) => o.kind === "content_provider");
  const byId = new Map(data.organizations.map((o) => [o.organization_id, o]));

  function close() {
    setAdding(false);
    setName("");
    setProblem(null);
  }

  async function create() {
    setBusy(true);
    setProblem(null);
    try {
      await api.post("/organizations", { name: name.trim() });
      close();
      reload();
    } catch (e) {
      setProblem(said(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="gl pp">
      <SectionHead title="Content providers" level={2} addLabel="New content provider"
                   adding={adding} onAdd={() => (adding ? close() : setAdding(true))}
                   disabledWhy={data.powers.platform ? null
                     : "Creating a content provider is for an operator."} />
      {adding && (
        <AddForm saveLabel="Create" missing={name.trim() ? null : "Enter a name."} busy={busy} error={problem}
                 onSave={create} onCancel={close}>
          <label>
            Name
            <input value={name} autoFocus onChange={(e) => setName(e.target.value)} />
          </label>
        </AddForm>
      )}
      {providers.length === 0 ? <p className="rv-mut">None yet.</p> : (
        <table className="tbl">
          <thead>
            <tr><th>Content provider</th><th>Tenant</th><th className="r">Content teams</th>
                <th className="r">Claimed domains</th><th /></tr>
          </thead>
          <tbody>
            {providers.map((p) => {
              const teams = p.children.map((id) => byId.get(id)).filter(
                (t): t is Org => !!t && t.kind === "team");
              return [
                <tr key={p.organization_id}>
                  <td>{p.name}</td>
                  <td>{p.home_tenant_id ? <code>{p.home_tenant_id}</code>
                                        : <span className="rv-mut">none yet</span>}</td>
                  <td className="r">{teams.length}</td>
                  <td className="r">{p.domains.length}</td>
                  <td className="r">
                    <button className="btn-link" aria-expanded={open === p.organization_id}
                            onClick={() => setOpen(open === p.organization_id ? null
                                                                               : p.organization_id)}>
                      {open === p.organization_id ? "Hide" : "Open"}
                    </button>
                  </td>
                </tr>,
                open === p.organization_id && (
                  <tr key={`${p.organization_id}:panel`} className="pp-add">
                    <td colSpan={5}>
                      <p className="rv-mut">{tenantLine(p)}</p>
                      <Teams provider={p} teams={teams} onChanged={reload} />
                      <SharedWith org={p} orgs={data.organizations} onChanged={reload} />
                      <DomainsPanel org={p} choices={data.sign_in_choices} onChanged={reload} />
                    </td>
                  </tr>
                ),
              ];
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Teams({ provider, teams, onChanged }: {
  provider: Org; teams: Org[]; onChanged: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function close() {
    setAdding(false);
    setName("");
    setError(null);
  }

  async function add() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/organizations/${encodeURIComponent(provider.organization_id)}/teams`,
                     { name: name.trim() });
      close();
      onChanged();
    } catch (e) {
      setError(said(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="pp-panel">
      <SectionHead title="Content teams" addLabel={`Add a content team to ${provider.name}`}
                   adding={adding} onAdd={() => (adding ? close() : setAdding(true))}
                   disabledWhy={provider.can.add_team ? null
                     : "Adding a content team is for an operator or the provider's configurators."} />
      {adding && (
        <AddForm saveLabel="Add" missing={name.trim() ? null : "Enter the team's name."} busy={busy} error={error}
                 onSave={add} onCancel={close}>
          <label>
            Content team name
            <input value={name} placeholder="e.g. ELA" autoFocus
                   onChange={(e) => setName(e.target.value)} />
          </label>
        </AddForm>
      )}
      {teams.length === 0 ? (
        <p className="rv-mut">None yet. A content team is a group of the provider's people that can
          be named, all at once, as a curriculum's writers.</p>
      ) : (
        <table className="tbl">
          <thead><tr><th>Content team</th></tr></thead>
          <tbody>{teams.map((t) => <tr key={t.organization_id}><td>{t.name}</td></tr>)}</tbody>
        </table>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------- Platform

export function PlatformScreen() {
  const { data, error, reload } = useOrganizations();
  if (error) return <Refused title="Platform" error={error} />;
  if (!data) return <PageSkeleton />;
  const platform = data.organizations.filter((o) => o.kind === "platform");
  return (
    <div className="gl pp">
      <h2>Platform</h2>
      {platform.map((p) => (
        <section key={p.organization_id} className="org">
          <h3>{p.name}</h3>
          <SharedWith org={p} orgs={data.organizations} onChanged={reload} />
          <DomainsPanel org={p} choices={data.sign_in_choices} onChanged={reload} />
        </section>
      ))}
      <section className="org">
        <h3>Tenants</h3>
        <p className="rv-mut">
          A tenant locks data rows. Each person reaches their own organization's tenant and
          Public. This list is information: a tenant is created with its district, and a school
          joins one on the Districts screen.
        </p>
        <table className="tbl">
          <thead>
            <tr><th>Tenant</th><th>Id</th><th>Type</th><th>Home of</th><th className="r">Schools</th></tr>
          </thead>
          <tbody>
            {data.tenants.map((t) => (
              <tr key={t.tenant_id}>
                <td>{t.name}</td>
                <td><code>{t.tenant_id}</code></td>
                <td>{t.tenant_type}</td>
                <td>{t.tenant_id === "public" ? "nobody: every signed-in person reads it"
                  : t.home_of ?? ""}</td>
                <td className="r">{t.tenant_id === "public" ? "every" : t.schools}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
